import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Star, ChefHat, User, Wine, CakeSlice, Utensils,
  MapPin, Calendar, Leaf, Send, Award
} from "lucide-react";
import { BADGE_COLORS } from "./badgeColors";

const MESSAGE_TEMPLATES = [
  "Vielen Dank für Ihren Besuch! Bei Ihrem nächsten Besuch erhalten Sie einen Aperitif aufs Haus. 🥂",
  "Wir freuen uns auf Ihren Besuch und bereiten gerne etwas Besonderes für Sie vor. ✨",
  "Als Dankeschön für Ihre Treue erwartet Sie beim nächsten Besuch ein Gruß aus der Küche. 👨‍🍳"
];

const CUISINE_LABELS = {
  "classic-french": "Classic French",
  "avant-garde": "Avant-garde",
  "norwegian": "Norwegian",
  "plant-based": "Plant-based",
  "sauce-focused": "Sauce-focused"
};

const getCuisineLabel = (id) => CUISINE_LABELS[id] || id;

const PreferenceBadges = ({ icon: Icon, label, items, variant, className, keyPrefix }) => {
  if (!items?.length) return null;
  return (
    <div className="mb-4">
      <p className="text-sm text-muted-foreground mb-2 flex items-center gap-2">
        <Icon className="w-4 h-4" />{label}
      </p>
      <div className="flex flex-wrap gap-2">
        {items.map((item) => (
          <Badge key={`${keyPrefix}-${item}`} variant={variant} className={className}>{item}</Badge>
        ))}
      </div>
    </div>
  );
};

const StammgastCard = ({ info, loyaltyProgram }) => (
  <Card
    className={info.count > 0 ? "bg-amber-500/10 border-amber-500/30" : "bg-card border-border"}
    data-testid="regular-guest-card"
  >
    <CardContent className="p-4 flex items-center gap-4">
      <Award className={`w-8 h-8 shrink-0 ${info.count > 0 ? 'text-amber-600' : 'text-muted-foreground'}`} />
      <div className="flex-1">
        {info.count > 0 ? (
          <>
            <p className="font-semibold text-amber-800">
              {info.count >= 2 ? "Stammgast" : "Hat Sie bereits besucht"}
            </p>
            <p className="text-sm text-muted-foreground">
              {info.count}× bei Ihnen ({info.restaurant_name})
              {info.last_visit && (
                <> · zuletzt am {new Date(info.last_visit).toLocaleDateString('de-DE')}</>
              )}
            </p>
          </>
        ) : (
          <>
            <p className="font-semibold">Noch kein Besuch bei Ihnen</p>
            <p className="text-sm text-muted-foreground">
              Dieser Gast hat {info.restaurant_name} noch nicht besucht – vielleicht bald!
            </p>
          </>
        )}
        {loyaltyProgram?.enabled && (
          <div className="mt-2 pt-2 border-t border-amber-500/20" data-testid="loyalty-progress">
            {info.count >= loyaltyProgram.threshold ? (
              <p className="text-xs font-medium text-green-700">
                🎁 Treue-Belohnung erreicht: {loyaltyProgram.reward}
              </p>
            ) : (
              <p className="text-xs text-muted-foreground">
                Treueprogramm: {info.count} / {loyaltyProgram.threshold} Besuche bis zur Belohnung
              </p>
            )}
          </div>
        )}
      </div>
    </CardContent>
  </Card>
);

const GuestDetails = ({
  guest, loyaltyProgram, messageText, setMessageText, sendingMessage, onSendMessage,
  fullHistory, loadingHistory, onToggleHistory
}) => {
  if (!guest) {
    return (
      <Card className="bg-card border-border h-full min-h-[300px] flex items-center justify-center">
        <CardContent className="text-center">
          <User className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">Wählen Sie einen Gast, um seine Vorlieben zu sehen</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="guest-details">
      <h2 className="text-xl font-semibold">Gast-Details</h2>

      {guest.visits_to_own_restaurant && <StammgastCard info={guest.visits_to_own_restaurant} loyaltyProgram={loyaltyProgram} />}

      {/* Guest Info Card */}
      <Card className="bg-card border-border">
        <CardContent className="p-6">
          <div className="flex items-center gap-4 mb-6">
            {guest.user.picture ? (
              <img src={guest.user.picture} alt="" className="w-16 h-16 rounded-full" />
            ) : (
              <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center">
                <User className="w-8 h-8 text-muted-foreground" />
              </div>
            )}
            <div>
              <p className="text-xl font-semibold">{guest.user.name}</p>
              <p className={`text-sm ${BADGE_COLORS[guest.user.badge_tier]}`}>
                {guest.user.badge_tier}
              </p>
              {guest.user.date_of_birth && (
                <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                  <Calendar className="w-3 h-3" />
                  DOB: {guest.user.date_of_birth}
                </p>
              )}
            </div>
          </div>

          {guest.preferences?.cuisines?.length > 0 && (
            <div className="mb-4">
              <p className="text-sm text-muted-foreground mb-2 flex items-center gap-2">
                <ChefHat className="w-4 h-4" />Cuisine Preferences
              </p>
              <div className="flex flex-wrap gap-2">
                {guest.preferences.cuisines.map((c) => (
                  <Badge key={c} variant="outline" className="border-primary text-primary">
                    {getCuisineLabel(c)}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {guest.preferences?.favorite_desserts?.length > 0 && (
            <div className="mb-4 p-4 border border-primary/20 rounded bg-primary/5">
              <p className="text-sm text-primary mb-2 flex items-center gap-2 font-semibold">
                <CakeSlice className="w-4 h-4" />Favorite Desserts
              </p>
              <div className="flex flex-wrap gap-2">
                {guest.preferences.favorite_desserts.map((d) => (
                  <Badge key={`dessert-${d}`} className="bg-primary/20 text-primary border-primary/30">{d}</Badge>
                ))}
              </div>
            </div>
          )}

          <PreferenceBadges icon={Utensils} label="Favorite Dishes" items={guest.preferences?.favorite_dishes} variant="secondary" keyPrefix="dish" />
          <PreferenceBadges icon={Wine} label="Favorite Drinks" items={guest.preferences?.favorite_drinks} variant="secondary" keyPrefix="drink" />
          <PreferenceBadges icon={Wine} label="Sauce Preferences" items={guest.preferences?.sauce_preferences} variant="outline" className="border-border" keyPrefix="sauce" />
          <PreferenceBadges icon={Leaf} label="Dietary Restrictions" items={guest.preferences?.dietary_restrictions} variant="destructive" keyPrefix="restriction" />

          {guest.preferences?.notes && (
            <div className="mb-4">
              <p className="text-sm text-muted-foreground mb-2">Additional Notes</p>
              <p className="text-sm italic bg-muted/20 p-3 rounded">
                "{guest.preferences.notes}"
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Send Message to Guest */}
      <Card className="bg-card border-border" data-testid="guest-message-card">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Send className="w-5 h-5 text-primary" />Nachricht an den Gast
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-xs text-muted-foreground">
            Bedanken Sie sich für den Besuch oder laden Sie den Gast mit einem Angebot ein.
            Die Nachricht erscheint in den Benachrichtigungen des Gastes.
          </p>
          <div className="flex flex-wrap gap-2">
            {MESSAGE_TEMPLATES.map((tpl, i) => (
              <button
                key={`tpl-${i}`}
                onClick={() => setMessageText(tpl)}
                className="text-xs px-3 py-1.5 rounded-full border border-border hover:border-primary hover:text-primary transition-colors text-left"
                data-testid={`message-template-${i}`}
              >
                {tpl.length > 55 ? tpl.slice(0, 55) + "…" : tpl}
              </button>
            ))}
          </div>
          <Textarea
            placeholder="z.B. Danke für Ihren Besuch – beim nächsten Mal erwartet Sie ein Aperitif aufs Haus!"
            value={messageText}
            onChange={(e) => setMessageText(e.target.value)}
            maxLength={500}
            className="bg-transparent border-border focus:border-primary"
            data-testid="guest-message-input"
          />
          <Button
            onClick={onSendMessage}
            disabled={sendingMessage || !messageText.trim()}
            className="w-full bg-primary text-primary-foreground hover:bg-primary/90"
            data-testid="guest-message-send"
          >
            <Send className="w-4 h-4 mr-2" />
            {sendingMessage ? "Wird gesendet..." : "Nachricht senden"}
          </Button>
        </CardContent>
      </Card>

      {/* Dining History */}
      {(guest.recent_visits?.length > 0 || fullHistory) && (
        <Card className="bg-card border-border" data-testid="dining-history-card">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <MapPin className="w-5 h-5 text-primary" />
              {fullHistory ? `Gesamte Sterne-Historie (${fullHistory.length})` : "Letzte Besuche"}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className={`space-y-2 ${fullHistory ? 'max-h-80 overflow-y-auto pr-2' : ''}`}>
              {(fullHistory || guest.recent_visits).map((visit) => (
                <div key={`visit-${visit.visit_id || `${visit.restaurant_id}-${visit.visit_date}`}`} className="flex items-center justify-between p-2 bg-muted/20 rounded">
                  <div>
                    <p className="font-medium text-sm">{visit.restaurant_name}</p>
                    <p className="text-xs text-muted-foreground">
                      {visit.visit_date}
                      {visit.visit_count > 1 && ` · ${visit.visit_count}× besucht`}
                    </p>
                  </div>
                  <div className="flex gap-0.5">
                    {[...Array(visit.stars)].map((_, j) => (
                      <Star key={`star-${visit.restaurant_id}-${visit.visit_date}-${j}`} className="w-3 h-3 text-primary fill-primary" />
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={onToggleHistory}
              disabled={loadingHistory}
              className="w-full mt-3 text-primary hover:text-primary"
              data-testid="toggle-full-history"
            >
              {loadingHistory
                ? "Wird geladen..."
                : fullHistory
                  ? "Nur letzte Besuche anzeigen"
                  : "Gesamte Sterne-Historie anzeigen"}
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default GuestDetails;
